/**
 * Aggiornare a comando fatture e incassi da Fatture in Cloud.
 *
 * Gli incassi si segnano su Fatture in Cloud, e Pixel li rilegge ogni notte.
 * Chi ha appena segnato un pagamento non deve aspettare domani: con questo
 * si fa lo stesso giro della notte, ma solo sulle fatture attive. Un giro
 * parziale non cancella mai niente: il confronto delle cancellazioni parte
 * solo nel giro completo (vedi supabase/functions/fic-sync).
 */

import { supabase } from './supabase';

/** Gli stessi ruoli che fic-sync accetta da un utente collegato. */
const RUOLI_AGGIORNAMENTO = ['admin', 'amministrazione'];

export function puoAggiornareDaFic(ruolo: string | null | undefined): boolean {
  return !!ruolo && RUOLI_AGGIORNAMENTO.includes(ruolo);
}

export interface EsitoAggiornamentoFic {
  /** Documenti letti: fatture, note di credito, pro forma. */
  documenti: number;
  incassati: number;
  daIncassare: number;
  stornati: number;
}

const numero = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * Il resoconto di fic-sync, o un errore. La funzione risponde "ok" anche
 * quando una singola parte e' fallita, e scrive l'errore al posto del
 * conteggio: qui quell'errore torna a essere un errore.
 */
export function leggiEsitoSync(dati: unknown): EsitoAggiornamentoFic {
  if (typeof dati !== 'object' || dati === null) {
    throw new Error('Risposta di Fatture in Cloud non leggibile');
  }
  const esito = dati as Record<string, unknown>;
  if (esito.ok !== true) {
    throw new Error(typeof esito.errore === 'string' ? esito.errore : 'Aggiornamento non riuscito');
  }
  if (typeof esito.fatture_attive === 'string') {
    throw new Error(esito.fatture_attive.replace(/^ERRORE:\s*/, ''));
  }
  return {
    documenti: numero(esito.fatture_attive),
    incassati: numero(esito.documenti_incassati),
    daIncassare: numero(esito.documenti_da_incassare),
    stornati: numero(esito.documenti_stornati),
  };
}

export interface EsitoAggiornamentoPassive {
  /** Spese registrate lette. */
  registrate: number;
  /** Fatture nella coda "Da registrare". */
  daRegistrare: number;
  /** Parti saltate (un tipo o un canale che Fatture in Cloud non ha dato). */
  avvisi: string[];
}

/** Le parti del giro passive che, se saltate, vanno dette. */
const PARTI_PASSIVE = /^fatture_passive_(expense|passive_credit_note|passive_delivery_note|da_registrare_(agyo|mail|browser))$/;

/** Il resoconto di fic-sync sulle fatture passive, o un errore. */
export function leggiEsitoSyncPassive(dati: unknown): EsitoAggiornamentoPassive {
  if (typeof dati !== 'object' || dati === null) {
    throw new Error('Risposta di Fatture in Cloud non leggibile');
  }
  const esito = dati as Record<string, unknown>;
  if (esito.ok !== true) {
    throw new Error(typeof esito.errore === 'string' ? esito.errore : 'Aggiornamento non riuscito');
  }
  if (typeof esito.fatture_passive === 'string') {
    throw new Error(esito.fatture_passive.replace(/^ERRORE:\s*/, ''));
  }
  const avvisi = Object.entries(esito)
    .filter(([chiave, valore]) => PARTI_PASSIVE.test(chiave) && typeof valore === 'string')
    .map(([chiave, valore]) => `${chiave.replace('fatture_passive_', '')}: ${String(valore)}`);
  return {
    registrate: numero(esito.fatture_passive),
    daRegistrare: numero(esito.fatture_passive_da_registrare),
    avvisi,
  };
}

/** Rilegge da Fatture in Cloud le fatture passive, comprese quelle da registrare. */
export async function aggiornaPassiveDaFattureInCloud(): Promise<EsitoAggiornamentoPassive> {
  const { data, error } = await supabase.functions.invoke('fic-sync', {
    body: { entita: ['fatture_passive'] },
  });
  if (error) throw error;
  return leggiEsitoSyncPassive(data);
}

/** Rilegge da Fatture in Cloud le fatture attive e i loro incassi. */
export async function aggiornaDaFattureInCloud(): Promise<EsitoAggiornamentoFic> {
  const { data, error } = await supabase.functions.invoke('fic-sync', {
    body: { entita: ['fatture_attive'] },
  });
  if (error) throw error;
  return leggiEsitoSync(data);
}

/** Quando le fatture attive (o passive) sono state aggiornate l'ultima volta, o null. */
export async function leggiUltimoAggiornamento(
  entita: 'fatture_attive' | 'fatture_passive' = 'fatture_attive',
): Promise<string | null> {
  const { data, error } = await supabase
    .from('fic_sync_state')
    .select('last_success_at')
    .eq('entity', entita)
    .maybeSingle();
  // Chi non puo' leggere lo stato della sincronizzazione semplicemente non
  // vede l'orario: non e' un errore da mostrare.
  if (error) return null;
  return data?.last_success_at ?? null;
}
